import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Shield, LogOut, Download, Lock, Plus, Trash2, X, Check, Mail, Clock, CheckCircle2, XCircle, AlertTriangle, Fingerprint } from 'lucide-react';
import { Transaction, Bill, Period, User, AccountRequest } from '../types';
import { format } from 'date-fns';
import { validatePasswordStrength, generateSecurePassword, sanitizeInput, isBiometricSupported, getBiometricName, registerBiometric, hasBiometricEnabled, removeBiometric } from '../utils/security';
interface AccountPageProps {
  currentUser: User;
  onLogout: () => void;
  onUpdatePassword: (newPassword: string) => Promise<boolean>;
  onAddUser?: (username: string, password: string) => Promise<boolean>;
  onDeleteUser?: (username: string) => boolean;
  getAllUsers?: () => User[];
  transactions: Transaction[];
  bills: Bill[];
  period: Period;
  date: Date;
}
export function AccountPage({
  currentUser,
  onLogout,
  onUpdatePassword,
  onAddUser,
  onDeleteUser,
  getAllUsers,
  transactions,
  bills,
  period,
  date
}: AccountPageProps) {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [message, setMessage] = useState('');
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [generatedCredentials, setGeneratedCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const isAdmin = currentUser.role === 'admin';
  const users = isAdmin && getAllUsers ? getAllUsers() : [];
  // Check biometric support and status
  useEffect(() => {
    setBiometricSupported(isBiometricSupported());
    setBiometricEnabled(hasBiometricEnabled(currentUser.username));
  }, [currentUser.username]);
  // Load requests for admin
  useEffect(() => {
    if (isAdmin) {
      const stored = localStorage.getItem('account_requests');
      if (stored) {
        const allRequests = JSON.parse(stored) as AccountRequest[];
        setRequests(allRequests.filter(r => r.status === 'pending'));
      }
    }
  }, [isAdmin]);
  const handleEnableBiometric = async () => {
    try {
      const success = await registerBiometric(currentUser.username);
      if (success) {
        setBiometricEnabled(true);
        setMessage(`${getBiometricName()} enabled successfully!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to enable biometric login. Please try again.');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Biometric authentication is not available on this device.');
      setTimeout(() => setMessage(''), 3000);
    }
  };
  const handleDisableBiometric = () => {
    if (confirm('Disable biometric login? You will need to use your password to sign in.')) {
      removeBiometric(currentUser.username);
      setBiometricEnabled(false);
      setMessage('Biometric login disabled');
      setTimeout(() => setMessage(''), 2000);
    }
  };
  const handlePasswordChange = async () => {
    const validation = validatePasswordStrength(newPassword);
    if (!validation.isValid) {
      setPasswordErrors(validation.errors);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordErrors(['Passwords do not match']);
      return;
    }
    const success = await onUpdatePassword(newPassword);
    if (success) {
      setMessage('Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
      setTimeout(() => {
        setShowPasswordModal(false);
        setMessage('');
      }, 1500);
    }
  };
  const handleAddUser = async () => {
    const sanitizedUsername = sanitizeInput(newUsername.trim());
    if (!sanitizedUsername || sanitizedUsername.length < 3) {
      setMessage('Username must be at least 3 characters');
      setTimeout(() => setMessage(''), 2000);
      return;
    }
    const validation = validatePasswordStrength(newUserPassword);
    if (!validation.isValid) {
      setMessage(validation.errors[0]);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const success = await onAddUser?.(sanitizedUsername, newUserPassword);
    if (success) {
      setMessage('User added successfully');
      setNewUsername('');
      setNewUserPassword('');
      setTimeout(() => {
        setShowAddUserModal(false);
        setMessage('');
      }, 1500);
    } else {
      setMessage('Username already exists');
      setTimeout(() => setMessage(''), 2000);
    }
  };
  const handleDeleteUser = (username: string) => {
    if (confirm(`Delete user "${username}"? This will remove all their data.`)) {
      const success = onDeleteUser?.(username);
      if (success) {
        setMessage('User deleted successfully');
        setTimeout(() => setMessage(''), 2000);
      }
    }
  };
  const handleApproveRequest = async (request: AccountRequest) => {
    // Generate username from email (before @)
    const baseUsername = sanitizeInput(request.email.split('@')[0]);
    const tempPassword = generateSecurePassword(12);
    const success = await onAddUser?.(baseUsername, tempPassword);
    if (success) {
      // Update request status
      const allRequests = JSON.parse(localStorage.getItem('account_requests') || '[]') as AccountRequest[];
      const updated = allRequests.map(r => r.id === request.id ? {
        ...r,
        status: 'approved' as const
      } : r);
      localStorage.setItem('account_requests', JSON.stringify(updated));
      setRequests(requests.filter(r => r.id !== request.id));
      // Show credentials
      setGeneratedCredentials({
        username: baseUsername,
        password: tempPassword
      });
      setMessage('User created successfully! Save these credentials.');
      setTimeout(() => {
        setGeneratedCredentials(null);
        setMessage('');
      }, 30000); // 30 seconds to copy credentials
    }
  };
  const handleRejectRequest = (requestId: string) => {
    const allRequests = JSON.parse(localStorage.getItem('account_requests') || '[]') as AccountRequest[];
    const updated = allRequests.map(r => r.id === requestId ? {
      ...r,
      status: 'rejected' as const
    } : r);
    localStorage.setItem('account_requests', JSON.stringify(updated));
    setRequests(requests.filter(r => r.id !== requestId));
    setMessage('Request rejected');
    setTimeout(() => setMessage(''), 2000);
  };
  const downloadData = () => {
    const periodData = transactions.filter(t => {
      const tDate = new Date(t.date);
      if (period === 'day') {
        return format(tDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
      }
      if (period === 'month') {
        return format(tDate, 'yyyy-MM') === format(date, 'yyyy-MM');
      }
      return format(tDate, 'yyyy') === format(date, 'yyyy');
    });
    const income = periodData.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expenses = periodData.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const savings = periodData.filter(t => t.type === 'savings').reduce((sum, t) => sum + t.amount, 0);
    const content = `
SMART BUDGET - FINANCIAL REPORT
================================

User: ${currentUser.username}
Period: ${period.toUpperCase()}
Date: ${format(date, period === 'day' ? 'MMMM dd, yyyy' : period === 'month' ? 'MMMM yyyy' : 'yyyy')}
Generated: ${format(new Date(), 'MMMM dd, yyyy HH:mm')}

SUMMARY
-------
Total Income:    $${income.toLocaleString('en-US', {
      minimumFractionDigits: 2
    })}
Total Expenses:  $${expenses.toLocaleString('en-US', {
      minimumFractionDigits: 2
    })}
Total Savings:   $${savings.toLocaleString('en-US', {
      minimumFractionDigits: 2
    })}
Net Balance:     $${(income - expenses - savings).toLocaleString('en-US', {
      minimumFractionDigits: 2
    })}

TRANSACTIONS
------------
${periodData.length === 0 ? 'No transactions in this period.' : ''}
${periodData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(t => {
      const sign = t.type === 'income' ? '+' : '-';
      return `${format(new Date(t.date), 'MMM dd, yyyy')} | ${t.type.toUpperCase().padEnd(8)} | ${sign}$${t.amount.toLocaleString('en-US', {
        minimumFractionDigits: 2
      }).padStart(12)} | ${t.name}`;
    }).join('\n')}

BILLS
-----
${bills.length === 0 ? 'No bills recorded.' : ''}
${bills.map(b => {
      const status = b.isPaid ? 'PAID' : 'PENDING';
      return `${b.name.padEnd(20)} | $${b.amount.toLocaleString('en-US', {
        minimumFractionDigits: 2
      }).padStart(10)} | Due: Day ${b.dueDate} | ${status}`;
    }).join('\n')}

================================
End of Report
`;
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smart_budget_${currentUser.username}_${format(date, 'yyyy-MM-dd')}_${period}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setMessage('Data downloaded successfully');
    setTimeout(() => setMessage(''), 2000);
  };
  return <div className="space-y-6 pb-24" data-id="element-137">
      <h1 className="text-2xl font-bold text-stone-50 px-2" data-id="element-138">Account</h1>

      {/* User Info Card */}
      <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-3xl p-6 border border-stone-800 shadow-xl" data-id="element-139">
        <div className="flex items-center gap-4 mb-4" data-id="element-140">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-141">
            {isAdmin ? <Shield size={32} className="text-white" data-id="element-142" /> : <UserIcon size={32} className="text-white" data-id="element-143" />}
          </div>
          <div data-id="element-144">
            <h2 className="text-2xl font-bold text-stone-50" data-id="element-145">
              {currentUser.username}
            </h2>
            <p className="text-stone-400 text-sm" data-id="element-146">
              {isAdmin ? 'Administrator' : 'User'}
            </p>
          </div>
        </div>
        <p className="text-stone-500 text-xs" data-id="element-147">
          Member since {format(new Date(currentUser.createdAt), 'MMMM yyyy')}
        </p>
      </div>

      {/* Generated Credentials Alert */}
      <AnimatePresence data-id="element-148">
        {generatedCredentials && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4" data-id="element-149">
            <div className="flex items-start gap-3" data-id="element-150">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} data-id="element-151" />
              <div className="flex-1" data-id="element-152">
                <h3 className="text-amber-500 font-bold mb-2" data-id="element-153">
                  Save These Credentials
                </h3>
                <div className="space-y-1 text-sm" data-id="element-154">
                  <p className="text-stone-300" data-id="element-155">
                    <span className="text-stone-500" data-id="element-156">Username:</span>{' '}
                    <span className="font-mono font-bold" data-id="element-157">
                      {generatedCredentials.username}
                    </span>
                  </p>
                  <p className="text-stone-300" data-id="element-158">
                    <span className="text-stone-500" data-id="element-159">Password:</span>{' '}
                    <span className="font-mono font-bold" data-id="element-160">
                      {generatedCredentials.password}
                    </span>
                  </p>
                </div>
                <p className="text-stone-500 text-xs mt-2" data-id="element-161">
                  Share these with the user. They won't be shown again.
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Account Requests (Admin Only) */}
      {isAdmin && requests.length > 0 && <div className="space-y-4" data-id="element-162">
          <h3 className="text-lg font-bold text-stone-50 px-2 flex items-center gap-2" data-id="element-163">
            Account Requests
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full" data-id="element-164">
              {requests.length}
            </span>
          </h3>

          <div className="space-y-3" data-id="element-165">
            {requests.map(request => <motion.div key={request.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-stone-800 rounded-2xl p-5 border border-stone-700/30" data-id="element-166">
                <div className="flex items-start justify-between mb-3" data-id="element-167">
                  <div className="flex-1" data-id="element-168">
                    <h4 className="text-stone-100 font-bold text-lg" data-id="element-169">
                      {request.fullName}
                    </h4>
                    <div className="flex items-center gap-2 text-stone-400 text-sm mt-1" data-id="element-170">
                      <Mail size={14} data-id="element-171" />
                      {request.email}
                    </div>
                    <div className="flex items-center gap-2 text-stone-500 text-xs mt-1" data-id="element-172">
                      <Clock size={12} data-id="element-173" />
                      {format(new Date(request.timestamp), 'MMM dd, yyyy HH:mm')}
                    </div>
                  </div>
                </div>

                {request.message && <div className="bg-stone-900/50 rounded-xl p-3 mb-3" data-id="element-174">
                    <p className="text-stone-300 text-sm italic" data-id="element-175">
                      "{request.message}"
                    </p>
                  </div>}

                <div className="flex gap-2" data-id="element-176">
                  <button onClick={() => handleApproveRequest(request)} className="flex-1 py-3 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors" data-id="element-177">
                    <CheckCircle2 size={16} data-id="element-178" />
                    Approve
                  </button>
                  <button onClick={() => handleRejectRequest(request.id)} className="flex-1 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors" data-id="element-179">
                    <XCircle size={16} data-id="element-180" />
                    Reject
                  </button>
                </div>
              </motion.div>)}
          </div>
        </div>}

      {/* Actions */}
      <div className="space-y-3" data-id="element-181">
        {/* Biometric Login Toggle */}
        {biometricSupported && <button onClick={biometricEnabled ? handleDisableBiometric : handleEnableBiometric} className="w-full bg-stone-800 hover:bg-stone-750 rounded-2xl p-4 flex items-center justify-between transition-colors border border-stone-700/30" data-id="element-182">
            <div className="flex items-center gap-3" data-id="element-183">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${biometricEnabled ? 'bg-emerald-500/10' : 'bg-stone-700'}`} data-id="element-184">
                <Fingerprint size={20} className={biometricEnabled ? 'text-emerald-500' : 'text-stone-400'} data-id="element-185" />
              </div>
              <div className="text-left" data-id="element-186">
                <span className="text-stone-100 font-medium block" data-id="element-187">
                  {getBiometricName()}
                </span>
                <span className="text-stone-500 text-xs" data-id="element-188">
                  {biometricEnabled ? 'Enabled' : 'Enable for quick login'}
                </span>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-bold ${biometricEnabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-stone-700 text-stone-400'}`} data-id="element-189">
              {biometricEnabled ? 'ON' : 'OFF'}
            </div>
          </button>}

        <button onClick={() => setShowPasswordModal(true)} className="w-full bg-stone-800 hover:bg-stone-750 rounded-2xl p-4 flex items-center justify-between transition-colors border border-stone-700/30" data-id="element-190">
          <div className="flex items-center gap-3" data-id="element-191">
            <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center" data-id="element-192">
              <Lock size={20} className="text-amber-500" data-id="element-193" />
            </div>
            <span className="text-stone-100 font-medium" data-id="element-194">Change Password</span>
          </div>
        </button>

        <button onClick={downloadData} className="w-full bg-stone-800 hover:bg-stone-750 rounded-2xl p-4 flex items-center justify-between transition-colors border border-stone-700/30" data-id="element-195">
          <div className="flex items-center gap-3" data-id="element-196">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center" data-id="element-197">
              <Download size={20} className="text-emerald-500" data-id="element-198" />
            </div>
            <div className="text-left" data-id="element-199">
              <span className="text-stone-100 font-medium block" data-id="element-200">
                Download Data
              </span>
              <span className="text-stone-500 text-xs" data-id="element-201">
                Export {period} data as text file
              </span>
            </div>
          </div>
        </button>

        <button onClick={onLogout} className="w-full bg-red-500/10 hover:bg-red-500/20 rounded-2xl p-4 flex items-center justify-between transition-colors border border-red-500/20" data-id="element-202">
          <div className="flex items-center gap-3" data-id="element-203">
            <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center" data-id="element-204">
              <LogOut size={20} className="text-red-500" data-id="element-205" />
            </div>
            <span className="text-red-400 font-medium" data-id="element-206">Sign Out</span>
          </div>
        </button>
      </div>

      {/* Admin Section */}
      {isAdmin && <div className="space-y-4 pt-6 border-t border-stone-800" data-id="element-207">
          <div className="flex items-center justify-between px-2" data-id="element-208">
            <h3 className="text-lg font-bold text-stone-50" data-id="element-209">User Management</h3>
            <button onClick={() => setShowAddUserModal(true)} className="p-2 bg-amber-500 text-stone-900 rounded-full hover:bg-amber-400 transition-colors" data-id="element-210">
              <Plus size={20} data-id="element-211" />
            </button>
          </div>

          <div className="space-y-2" data-id="element-212">
            {users.map(user => <div key={user.username} className="bg-stone-800 rounded-2xl p-4 flex items-center justify-between border border-stone-700/30" data-id="element-213">
                <div className="flex items-center gap-3" data-id="element-214">
                  <div className="w-10 h-10 bg-stone-700 rounded-xl flex items-center justify-center" data-id="element-215">
                    {user.role === 'admin' ? <Shield size={18} className="text-amber-500" data-id="element-216" /> : <UserIcon size={18} className="text-stone-400" data-id="element-217" />}
                  </div>
                  <div data-id="element-218">
                    <p className="text-stone-100 font-medium" data-id="element-219">
                      {user.username}
                    </p>
                    <p className="text-stone-500 text-xs capitalize" data-id="element-220">
                      {user.role}
                    </p>
                  </div>
                </div>
                {user.username !== 'MarOne' && <button onClick={() => handleDeleteUser(user.username)} className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors" data-id="element-221">
                    <Trash2 size={16} data-id="element-222" />
                  </button>}
              </div>)}
          </div>
        </div>}

      {/* Message Toast */}
      <AnimatePresence data-id="element-223">
        {message && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-stone-800 text-stone-50 px-6 py-3 rounded-2xl shadow-xl border border-stone-700 z-50 max-w-md text-center" data-id="element-224">
            {message}
          </motion.div>}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence data-id="element-225">
        {showPasswordModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowPasswordModal(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-226" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 bg-stone-900 rounded-t-3xl z-50 p-6 border-t border-stone-800 max-h-[90vh] overflow-y-auto" data-id="element-227">
              <div className="flex justify-between items-center mb-6" data-id="element-228">
                <h2 className="text-2xl font-bold text-stone-50" data-id="element-229">
                  Change Password
                </h2>
                <button onClick={() => setShowPasswordModal(false)} className="p-2 bg-stone-800 rounded-full text-stone-400 hover:text-stone-200" data-id="element-230">
                  <X size={20} data-id="element-231" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-232">
                {passwordErrors.length > 0 && <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3" data-id="element-233">
                    <ul className="text-red-400 text-sm space-y-1" data-id="element-234">
                      {passwordErrors.map((error, i) => <li key={i} data-id="element-235">• {error}</li>)}
                    </ul>
                  </div>}

                <div className="bg-stone-800/50 border border-stone-700 rounded-xl p-3" data-id="element-236">
                  <p className="text-stone-400 text-xs" data-id="element-237">
                    Password must contain:
                  </p>
                  <ul className="text-stone-500 text-xs mt-1 space-y-0.5" data-id="element-238">
                    <li data-id="element-239">• At least 8 characters</li>
                    <li data-id="element-240">• One uppercase letter</li>
                    <li data-id="element-241">• One lowercase letter</li>
                    <li data-id="element-242">• One number</li>
                    <li data-id="element-243">• One special character</li>
                  </ul>
                </div>

                <div data-id="element-244">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-245">
                    New Password
                  </label>
                  <input type="password" value={newPassword} onChange={e => {
                setNewPassword(e.target.value);
                setPasswordErrors([]);
              }} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="Enter new password" data-id="element-246" />
                </div>

                <div data-id="element-247">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-248">
                    Confirm Password
                  </label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="Confirm new password" data-id="element-249" />
                </div>

                <button onClick={handlePasswordChange} className="w-full py-4 bg-amber-500 text-stone-900 font-bold rounded-2xl hover:bg-amber-400 transition-colors flex items-center justify-center gap-2" data-id="element-250">
                  <Check size={20} data-id="element-251" />
                  Update Password
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Add User Modal */}
      <AnimatePresence data-id="element-252">
        {showAddUserModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowAddUserModal(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-253" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 bg-stone-900 rounded-t-3xl z-50 p-6 border-t border-stone-800" data-id="element-254">
              <div className="flex justify-between items-center mb-6" data-id="element-255">
                <h2 className="text-2xl font-bold text-stone-50" data-id="element-256">Add User</h2>
                <button onClick={() => setShowAddUserModal(false)} className="p-2 bg-stone-800 rounded-full text-stone-400 hover:text-stone-200" data-id="element-257">
                  <X size={20} data-id="element-258" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-259">
                <div data-id="element-260">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-261">
                    Username (min 3 characters)
                  </label>
                  <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="Enter username" minLength={3} maxLength={50} data-id="element-262" />
                </div>

                <div data-id="element-263">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-264">
                    Password (must be strong)
                  </label>
                  <input type="password" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="Enter password" data-id="element-265" />
                  <p className="text-stone-500 text-xs mt-2" data-id="element-266">
                    8+ chars, uppercase, lowercase, number, special character
                  </p>
                </div>

                <button onClick={handleAddUser} className="w-full py-4 bg-amber-500 text-stone-900 font-bold rounded-2xl hover:bg-amber-400 transition-colors flex items-center justify-center gap-2" data-id="element-267">
                  <Plus size={20} data-id="element-268" />
                  Add User
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}